import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBriefcase,
  FaCalendarAlt,
  FaClock,
  FaHospitalUser,
  FaMapMarkerAlt,
  FaUserMd,
} from "react-icons/fa";
import PublicHeader from "../../components/PublicHeader";
import PublicFooter from "../../components/PublicFooter";
import { supabase } from "../../supabaseClient";

function formatShiftDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date to be confirmed";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function formatShiftTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PublicHome({ session, profile }) {
  const [shifts, setShifts] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function loadPublicData() {
      const now = new Date().toISOString();

      const [shiftResult, vacancyResult] = await Promise.all([
        supabase
          .from("public_shift_calendar")
          .select("id, title, location, time")
          .gte("time", now)
          .order("time", { ascending: true })
          .limit(5),
        supabase
          .from("public_vacancies")
          .select(
            "id, title, description, department, location, closing_date, created_at",
          )
          .order("created_at", { ascending: false })
          .limit(3),
      ]);

      if (!active) return;

      if (!shiftResult.error) setShifts(shiftResult.data || []);
      if (!vacancyResult.error) setVacancies(vacancyResult.data || []);
      setLoading(false);
    }

    loadPublicData();
    return () => {
      active = false;
    };
  }, []);

  const todayShiftCount = useMemo(() => {
    const today = new Date().toDateString();
    return shifts.filter(
      (shift) => new Date(shift.time).toDateString() === today,
    ).length;
  }, [shifts]);

  return (
    <div className="pguh-public-page-shell">
      <PublicHeader session={session} profile={profile} />

      <main id="main-content" tabIndex="-1" className="pguh-public-home">
        <section className="pguh-public-hero">
          <div className="nhsuk-width-container pguh-public-hero__inner">
            <div className="pguh-public-hero__copy">
              <span className="pguh-public-kicker">
                Royal University Hospital Trust
              </span>
              <h1>
                Hospital services, opportunities and patient access in one place
              </h1>
              <p>
                View upcoming hospital sessions, explore current vacancies,
                access the patient portal or sign in to staff services.
              </p>
              <div className="pguh-public-hero__actions">
                <Link
                  className="pguh-public-button pguh-public-button--light"
                  to="/patient-portal"
                >
                  Patient portal <FaArrowRight aria-hidden="true" />
                </Link>
                <Link
                  className="pguh-public-button pguh-public-button--outline"
                  to="/vacancies"
                >
                  View vacancies
                </Link>
              </div>
            </div>

            <aside
              className="pguh-public-hero__status"
              aria-label="Hospital digital services"
            >
              <div>
                <span>Public service</span>
                <strong>Online</strong>
              </div>
              <div>
                <span>Upcoming shifts shown</span>
                <strong>{loading ? "…" : shifts.length}</strong>
              </div>
              <div>
                <span>Open vacancies</span>
                <strong>{loading ? "…" : vacancies.length}</strong>
              </div>
            </aside>
          </div>
        </section>

        <section
          className="nhsuk-width-container pguh-public-quicklinks"
          aria-label="Popular services"
        >
          <Link to="/patient-portal" className="pguh-public-service-card">
            <span className="pguh-public-service-card__icon">
              <FaHospitalUser aria-hidden="true" />
            </span>
            <span>
              <strong>Patient portal</strong>
              <small>View appointments and check in online.</small>
            </span>
            <FaArrowRight aria-hidden="true" />
          </Link>

          <Link to="/shifts" className="pguh-public-service-card">
            <span className="pguh-public-service-card__icon">
              <FaCalendarAlt aria-hidden="true" />
            </span>
            <span>
              <strong>Shift calendar</strong>
              <small>See when public hospital sessions are scheduled.</small>
            </span>
            <FaArrowRight aria-hidden="true" />
          </Link>

          <Link to="/vacancies" className="pguh-public-service-card">
            <span className="pguh-public-service-card__icon">
              <FaBriefcase aria-hidden="true" />
            </span>
            <span>
              <strong>Vacancies</strong>
              <small>Explore roles and submit an application online.</small>
            </span>
            <FaArrowRight aria-hidden="true" />
          </Link>

          <Link
            to={
              session && profile?.position !== "Patient"
                ? "/intranet/portal"
                : "/login"
            }
            className="pguh-public-service-card"
          >
            <span className="pguh-public-service-card__icon">
              <FaUserMd aria-hidden="true" />
            </span>
            <span>
              <strong>Staff services</strong>
              <small>Secure access to the RUHT staff intranet.</small>
            </span>
            <FaArrowRight aria-hidden="true" />
          </Link>
        </section>

        <section className="nhsuk-width-container pguh-public-home-grid">
          <div className="pguh-public-section-card">
            <div className="pguh-public-section-card__header">
              <div>
                <span className="pguh-public-kicker">What is happening</span>
                <h2>Upcoming shifts</h2>
              </div>
              <Link to="/shifts">Full calendar</Link>
            </div>

            {loading ? (
              <p className="pguh-public-muted">Loading upcoming shifts…</p>
            ) : shifts.length === 0 ? (
              <div className="pguh-public-empty">
                <FaCalendarAlt aria-hidden="true" />
                <p>No public shifts are currently scheduled.</p>
              </div>
            ) : (
              <div className="pguh-public-shift-list">
                {shifts.map((shift) => (
                  <article key={shift.id}>
                    <div className="pguh-public-shift-date">
                      <strong>{formatShiftDate(shift.time)}</strong>
                      <span>
                        <FaClock aria-hidden="true" />{" "}
                        {formatShiftTime(shift.time)}
                      </span>
                    </div>
                    <div>
                      <h3>{shift.title}</h3>
                      <p>
                        <FaMapMarkerAlt aria-hidden="true" />{" "}
                        {shift.location || "Prince George University Hospital"}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {!loading && todayShiftCount > 0 && (
              <p className="pguh-public-info-strip">
                {todayShiftCount} public shift
                {todayShiftCount === 1 ? " is" : "s are"} scheduled today.
              </p>
            )}
          </div>

          <div className="pguh-public-section-card">
            <div className="pguh-public-section-card__header">
              <div>
                <span className="pguh-public-kicker">Join RUHT</span>
                <h2>Current vacancies</h2>
              </div>
              <Link to="/vacancies">All vacancies</Link>
            </div>

            {loading ? (
              <p className="pguh-public-muted">Loading vacancies…</p>
            ) : vacancies.length === 0 ? (
              <div className="pguh-public-empty">
                <FaBriefcase aria-hidden="true" />
                <p>There are currently no open vacancies.</p>
              </div>
            ) : (
              <div className="pguh-public-vacancy-preview-list">
                {vacancies.map((vacancy) => (
                  <article key={vacancy.id}>
                    <span>Open</span>
                    <h3>{vacancy.title}</h3>
                    <p>
                      {vacancy.department ||
                        vacancy.location ||
                        "Royal University Hospitals Trust"}
                    </p>
                    <Link to={`/vacancies/${vacancy.id}`}>View vacancy</Link>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="pguh-public-patient-promo">
          <div className="nhsuk-width-container pguh-public-patient-promo__inner">
            <div>
              <span className="pguh-public-kicker">
                Digital patient services
              </span>
              <h2>Check in before you arrive</h2>
              <p>
                Patients with a linked Roblox account can view upcoming
                appointments and check in online when their appointment window
                opens.
              </p>
            </div>
            <Link className="pguh-public-button" to="/patient-portal">
              Open patient portal <FaArrowRight aria-hidden="true" />
            </Link>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
